import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import {
  createCamera,
  updateCamera,
  getCameraById,
  getAllAccessZones,
} from "../../../service/securityService";
import {
  Camera,
  CameraFormData,
  CameraType,
  CameraStatus,
  AccessZone,
} from "../../../types/security_types";

interface CameraFormProps {
  camera?: Camera;
  onSuccess: () => void;
  onCancel: () => void;
}

const CameraForm: React.FC<CameraFormProps> = ({
  camera,
  onSuccess,
  onCancel,
}) => {
  const isEdit = !!camera;
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [zones, setZones] = useState<AccessZone[]>([]);

  const [formData, setFormData] = useState<CameraFormData>({
    cameraId: "",
    cameraName: "",
    location: "",
    zoneId: "",
    zoneName: "",
    cameraType: "IP",
    vendor: "",
    ipAddress: "",
    status: "Active",
    streamUrl: "",
    playbackUrl: "",
    vmsProvider: "",
    vmsExternalId: "",
  });

  useEffect(() => {
    getAllAccessZones()
      .then(setZones)
      .catch(() => setZones([]));
  }, []);

  useEffect(() => {
    if (isEdit && camera) {
      loadCameraData();
    }
  }, [camera]);

  const loadCameraData = async () => {
    if (!camera) return;
    try {
      setLoading(true);
      const id = camera._id || camera.id;
      if (!id) return;
      const cameraData = await getCameraById(id);
      setFormData({
        cameraId: cameraData.cameraId || "",
        cameraName: cameraData.cameraName || "",
        location: cameraData.location || "",
        zoneId: cameraData.zoneId || "",
        zoneName: cameraData.zoneName || "",
        cameraType: cameraData.cameraType || "IP",
        vendor: cameraData.vendor || "",
        ipAddress: cameraData.ipAddress || "",
        status:
          cameraData.status === "Maintenance" ? "Maintenance" : "Active",
        streamUrl: cameraData.streamUrl || "",
        playbackUrl: cameraData.playbackUrl || "",
        vmsProvider: cameraData.vmsProvider || "",
        vmsExternalId: cameraData.vmsExternalId || "",
      });
    } catch (error: any) {
      console.error("Error loading camera data:", error);
      toast.error("Failed to load camera data");
    } finally {
      setLoading(false);
    }
  };

  const validateIP = (ip: string): boolean => {
    const ipRegex = /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
    return ipRegex.test(ip);
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.cameraId.trim()) {
      newErrors.cameraId = "Camera ID is required";
    }

    if (!formData.cameraName.trim()) {
      newErrors.cameraName = "Camera name is required";
    }

    if (!formData.ipAddress.trim()) {
      newErrors.ipAddress = "IP address is required";
    } else if (!validateIP(formData.ipAddress)) {
      newErrors.ipAddress = "Invalid IP address format";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error("Please fix the errors in the form");
      return;
    }

    try {
      setSubmitting(true);
      if (isEdit && camera) {
        const id = camera._id || camera.id;
        if (!id) return;
        await updateCamera(id, formData);
        toast.success("Camera updated successfully");
      } else {
        await createCamera(formData);
        toast.success("Camera created successfully");
      }
      onSuccess();
    } catch (error: any) {
      console.error("Error saving camera:", error);
      toast.error(error.response?.data?.resMessage || "Failed to save camera");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="p-4">Loading...</div>;
  }

  return (
    <>
      <h2 className="text-xl font-semibold text-gray-800 border-b px-6 pb-2 mb-4">
        {isEdit ? "Update Camera Details" : "Create New Camera"}
      </h2>
      <form onSubmit={handleSubmit} id="cameraForm" className="px-6 pb-6">
        <div className="flex flex-col gap-6">
          <div className="grid grid-cols-2 gap-4">
            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-gray-700">
                Camera ID <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.cameraId}
                onChange={(e) => {
                  setFormData({ ...formData, cameraId: e.target.value });
                  setErrors({ ...errors, cameraId: "" });
                }}
                className={`w-full p-2 border rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none ${
                  errors.cameraId ? "border-red-500" : "border-gray-300"
                }`}
                placeholder="Enter camera ID"
                required
              />
              {errors.cameraId && (
                <span className="text-red-500 text-xs">{errors.cameraId}</span>
              )}
            </div>

            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-gray-700">
                Camera Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.cameraName}
                onChange={(e) => {
                  setFormData({ ...formData, cameraName: e.target.value });
                  setErrors({ ...errors, cameraName: "" });
                }}
                className={`w-full p-2 border rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none ${
                  errors.cameraName ? "border-red-500" : "border-gray-300"
                }`}
                placeholder="Enter camera name"
                required
              />
              {errors.cameraName && (
                <span className="text-red-500 text-xs">{errors.cameraName}</span>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-gray-700">
                Access zone (optional)
              </label>
              <select
                value={formData.zoneId || ""}
                onChange={(e) => {
                  const zid = e.target.value;
                  const z = zones.find((x) => (x._id || x.id) === zid);
                  setFormData({
                    ...formData,
                    zoneId: zid,
                    zoneName: z?.zoneName || "",
                    location: z?.zoneName || formData.location,
                  });
                }}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none"
              >
                <option value="">— None —</option>
                {zones.map((z) => (
                  <option key={z._id || z.id} value={z._id || z.id}>
                    {z.zoneName} ({z.zoneCode})
                  </option>
                ))}
              </select>
            </div>
            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-gray-700">
                Location (Zone / Area)
              </label>
              <input
                type="text"
                value={formData.location}
                onChange={(e) => {
                  setFormData({ ...formData, location: e.target.value });
                }}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none"
                placeholder="e.g. ICU, Main Gate"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-gray-700">
                Camera Type <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.cameraType}
                onChange={(e) => {
                  setFormData({
                    ...formData,
                    cameraType: e.target.value as CameraType,
                  });
                }}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none"
                required
              >
                <option value="IP">IP Camera</option>
                <option value="PTZ">PTZ Camera</option>
                <option value="DOME">Dome Camera</option>
              </select>
            </div>

            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-gray-700">
                Status <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.status}
                onChange={(e) => {
                  setFormData({
                    ...formData,
                    status: e.target.value as CameraStatus,
                  });
                }}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none"
                required
              >
                <option value="Active">Active</option>
                <option value="Maintenance">Maintenance</option>
              </select>
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium text-gray-700">
              Vendor
            </label>
            <input
              type="text"
              value={formData.vendor}
              onChange={(e) => {
                setFormData({ ...formData, vendor: e.target.value });
              }}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none"
              placeholder="Enter vendor name"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="flex flex-col gap-2 col-span-2">
              <label className="text-sm font-medium text-gray-700">Live stream URL</label>
              <input
                type="url"
                value={formData.streamUrl || ""}
                onChange={(e) =>
                  setFormData({ ...formData, streamUrl: e.target.value })
                }
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none"
                placeholder="Browser-playable stream (e.g. MP4 or HLS endpoint)"
              />
            </div>
            <div className="flex flex-col gap-2 col-span-2">
              <label className="text-sm font-medium text-gray-700">Playback URL</label>
              <input
                type="url"
                value={formData.playbackUrl || ""}
                onChange={(e) =>
                  setFormData({ ...formData, playbackUrl: e.target.value })
                }
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none"
                placeholder="Recorded clip or archive player URL"
              />
            </div>
            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-gray-700">VMS vendor</label>
              <input
                type="text"
                value={formData.vmsProvider || ""}
                onChange={(e) =>
                  setFormData({ ...formData, vmsProvider: e.target.value })
                }
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none"
                placeholder="e.g. Genetec, Milestone"
              />
            </div>
            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-gray-700">VMS external ID</label>
              <input
                type="text"
                value={formData.vmsExternalId || ""}
                onChange={(e) =>
                  setFormData({ ...formData, vmsExternalId: e.target.value })
                }
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none"
                placeholder="Device ID in VMS"
              />
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium text-gray-700">
              IP Address <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.ipAddress}
              onChange={(e) => {
                setFormData({ ...formData, ipAddress: e.target.value });
                setErrors({ ...errors, ipAddress: "" });
              }}
              className={`w-full p-2 border rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none ${
                errors.ipAddress ? "border-red-500" : "border-gray-300"
              }`}
              placeholder="e.g., 192.168.1.100"
              required
            />
            {errors.ipAddress && (
              <span className="text-red-500 text-xs">{errors.ipAddress}</span>
            )}
          </div>
        </div>
      </form>
    </>
  );
};

export default CameraForm;
